// Manual RACI reassignment on the Analyze tab: which jobs may hold a role, and
// how a manual choice merges with the agent's own cheaper-job swaps.
//
// Both kinds of change end up in ONE override set that is sent to the backend
// what-if engine (never re-scored client-side) and persisted with the saved
// version, so a hand-picked job and an agent-proposed swap are treated
// identically by every downstream calculation.

export const RACI_ROLES = ["R", "A", "C", "I"];

export const ROLE_LABELS = {
  R: "Responsible",
  A: "Accountable",
  C: "Consulted",
  I: "Informed",
};

const num = (v) => {
  if (v === "" || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const text = (v) => String(v ?? "").trim();

/** Rate keys differ by payload branch: RACI entries use `hourly_rate`, the
 *  agent's replacement cards use `hourlyRate`. */
const rateOf = (o) =>
  num(o?.hourly_rate ?? o?.hourlyRate ?? o?.rate ?? o?.hourly_rate_usd);

/** One override identity = one role-holder on one task. Two different jobs
 *  holding the same role on the same task are separate slots, so the job being
 *  replaced is part of the key. */
export const overrideKey = (taskId, role, replaceJobId) =>
  `${taskId}::${String(role || "").toUpperCase()}::${replaceJobId ?? "none"}`;

const candidate = (raw, { currentJobId } = {}) => {
  const jobId = num(raw?.job_id ?? raw?.jobId);
  if (jobId == null) return null;
  const name = text(raw?.job_name ?? raw?.jobName ?? raw?.name);
  return {
    job_id: jobId,
    job_name: name || `Job ${jobId}`,
    hourly_rate: rateOf(raw),
    skill_match_percentage: num(
      raw?.skill_match_percentage ?? raw?.skillMatchPercentage,
    ),
    is_current:
      Boolean(raw?.is_current ?? raw?.isCurrent) ||
      (currentJobId != null && String(jobId) === String(currentJobId)),
  };
};

// Current holder first, then best skill match, then alphabetical — the same
// order the what-if picker uses, so the two pickers read alike.
const compareCandidates = (a, b) => {
  if (a.is_current !== b.is_current) return a.is_current ? -1 : 1;
  const match = (b.skill_match_percentage ?? -1) - (a.skill_match_percentage ?? -1);
  if (match !== 0) return match;
  return a.job_name.localeCompare(b.job_name);
};

const findTaskRow = (list, taskId) =>
  (Array.isArray(list) ? list : []).find(
    (r) => String(r?.task_id) === String(taskId),
  ) || null;

/**
 * Each job's rate as stated on its REAL assignments anywhere in the process.
 * Candidate lists have been observed to disagree with assignments for the same
 * job (a job listed at 5000/hr as a Consulted candidate but 17.99 where it is
 * actually assigned — see whatIfNormalize). Assignment rates reproduce the costs
 * the rest of the page shows, so they win, and the picker never previews a
 * saving computed from a rate nothing else agrees with.
 */
const collectAssignedRates = (optimizationData) => {
  const byJob = new Map();
  const rows = optimizationData?.raci_assignments?.matrix?.rows;
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    RACI_ROLES.forEach((role) => {
      const entries = row?.[role];
      (Array.isArray(entries) ? entries : entries ? [entries] : []).forEach((e) => {
        const jobId = num(e?.job_id);
        const rate = rateOf(e);
        if (jobId != null && rate != null && !e?.is_pool_suggestion && !byJob.has(String(jobId))) {
          byJob.set(String(jobId), rate);
        }
      });
    });
  });
  return byJob;
};

/**
 * Whether the payload carries a genuine per-role eligibility list for this task
 * — the only thing that licenses calling a job outside it a skill mismatch.
 * Pool suggestions and agent replacement targets are candidates, not lists, so
 * they deliberately do not count here.
 */
function hasRoleEligibleList(optimizationData, taskId, role) {
  const roleKey = String(role || "").toUpperCase();
  return [
    findTaskRow(optimizationData?.raci_assignments?.matrix?.rows, taskId),
    findTaskRow(optimizationData?.raci_assignments?.tasks, taskId),
  ].some((row) => {
    const perRole = row?.role_eligible_jobs || row?.roleEligibleJobs;
    const list = perRole?.[roleKey] ?? perRole?.[roleKey.toLowerCase()];
    return Array.isArray(list) && list.length > 0;
  });
}

/**
 * Jobs that may hold `role` on `taskId`, best candidate first.
 *
 * Source order matters. `role_eligible_jobs` is the authoritative per-role list
 * (the shape the what-if endpoint already returns and the optimize endpoint is
 * expected to adopt); until it is present, the optimize payload still carries
 * usable candidates in two other places, so the picker works today instead of
 * shipping an empty modal:
 *   1. `role_eligible_jobs[role]` on the RACI matrix row, then on the task.
 *   2. pool suggestions (`is_pool_suggestion`) — jobs the agent identified as
 *      possible replacements. They are filtered out of the RACI tables on
 *      purpose (they are not staffing), but they are exactly the right
 *      candidates here.
 *   3. the agent's own accepted-or-not replacement targets for this task+role.
 * The incumbent is always folded in, so the list is never empty when a job
 * currently holds the role.
 */
export function resolveEligibleJobs(optimizationData, taskId, role, currentJob) {
  const roleKey = String(role || "").toUpperCase();
  const currentJobId = currentJob?.job_id ?? currentJob?.jobId ?? null;
  const assignedRates = collectAssignedRates(optimizationData);
  const byId = new Map();

  const add = (raw) => {
    const c = candidate(raw, { currentJobId });
    if (!c) return;
    const key = String(c.job_id);
    const prev = byId.get(key);
    // Keep the richest record: a later source may supply a rate or skill match
    // the first one lacked.
    byId.set(key, {
      ...c,
      hourly_rate: c.hourly_rate ?? prev?.hourly_rate ?? null,
      skill_match_percentage:
        c.skill_match_percentage ?? prev?.skill_match_percentage ?? null,
      is_current: Boolean(c.is_current || prev?.is_current),
    });
  };

  const matrixRow = findTaskRow(
    optimizationData?.raci_assignments?.matrix?.rows,
    taskId,
  );
  const taskRow = findTaskRow(optimizationData?.raci_assignments?.tasks, taskId);

  // 1. authoritative per-role list
  [matrixRow, taskRow].forEach((row) => {
    const perRole = row?.role_eligible_jobs || row?.roleEligibleJobs;
    const list = perRole?.[roleKey] ?? perRole?.[roleKey?.toLowerCase()];
    (Array.isArray(list) ? list : []).forEach(add);
  });

  // 2. pool suggestions already present in the payload
  if (matrixRow) {
    const entries = matrixRow[roleKey];
    (Array.isArray(entries) ? entries : entries ? [entries] : [])
      .filter((e) => e?.is_pool_suggestion)
      .forEach(add);
  }
  if (taskRow) {
    const byRole = {
      R: taskRow.responsible,
      A: taskRow.accountable,
      C: taskRow.consulted,
      I: taskRow.informed,
    }[roleKey];
    (Array.isArray(byRole) ? byRole : byRole ? [byRole] : [])
      .filter((e) => e?.is_pool_suggestion)
      .forEach(add);
  }

  // 3. the agent's replacement targets for this task+role
  const replacements =
    optimizationData?.normalized?.costOptimization?.replacements || [];
  replacements
    .filter(
      (r) =>
        String(r?.task_id) === String(taskId) &&
        (!r?.raci_role || String(r.raci_role).toUpperCase() === roleKey),
    )
    .forEach((r) => {
      if (r?.new_job || r?.new_job_id != null) {
        add({
          job_id: r.new_job_id ?? r.new_job?.job_id,
          job_name: r.new_job?.name ?? r.new_job?.job_name,
          hourly_rate: rateOf(r.new_job),
        });
      }
    });

  // the incumbent, so the picker always shows where it started
  if (currentJobId != null) {
    add({
      job_id: currentJobId,
      job_name: currentJob?.job_name ?? currentJob?.name,
      hourly_rate: rateOf(currentJob),
      is_current: true,
    });
  }

  return [...byId.values()]
    .map((c) => {
      const assigned = assignedRates.get(String(c.job_id));
      return assigned != null ? { ...c, hourly_rate: assigned } : c;
    })
    .sort(compareCandidates);
}

/**
 * Every job the analyst may pick, annotated with skill advice.
 *
 * The reassignment is never blocked on skills: any job can be chosen, and a
 * skill problem is reported as advice — the same stance the page already takes
 * with the process-level skill-mismatch banner.
 *
 * `eligibility` is deliberately three-valued, because "not in the eligible list"
 * and "there is no eligible list" are different claims:
 *   - "eligible"     — the optimization named this job for this role (skill match
 *                      known and reported).
 *   - "mismatch"     — an eligible list EXISTS for this role and this job is not
 *                      in it, so the skills genuinely do not line up.
 *   - "unreported"   — no eligible list came back at all, so nothing is known
 *                      either way. Claiming a mismatch here would be inventing a
 *                      finding the payload never made.
 */
export function buildCandidateList({
  optimizationData,
  taskId,
  role,
  currentJob,
  allJobs,
}) {
  const eligible = resolveEligibleJobs(optimizationData, taskId, role, currentJob);
  const eligibleById = new Map(eligible.map((c) => [String(c.job_id), c]));
  const currentJobId = currentJob?.job_id ?? currentJob?.jobId ?? null;

  // ONLY a real per-role list counts as eligibility data. It is tempting to infer
  // it from `eligible` being non-empty, but that set also absorbs the agent's
  // replacement TARGETS — one suggested swap is not a statement about every other
  // job, so inferring from it would stamp "skill mismatch" on the whole company's
  // job list on the strength of a single suggestion.
  const hasEligibilityData = hasRoleEligibleList(optimizationData, taskId, role);

  const assignedRates = collectAssignedRates(optimizationData);
  const byId = new Map();

  // Start from the eligible set so a job the optimization named is present even
  // if the job list request failed or omitted it.
  eligible.forEach((c) => byId.set(String(c.job_id), { ...c, eligibility: "eligible" }));

  (Array.isArray(allJobs) ? allJobs : []).forEach((j) => {
    const c = candidate(j, { currentJobId });
    if (!c) return;
    const key = String(c.job_id);
    const known = eligibleById.get(key);
    byId.set(key, {
      ...c,
      job_name: known?.job_name || c.job_name,
      // Assignment rate > job-record rate > eligible-list rate.
      hourly_rate:
        assignedRates.get(key) ?? c.hourly_rate ?? known?.hourly_rate ?? null,
      skill_match_percentage:
        known?.skill_match_percentage ?? c.skill_match_percentage ?? null,
      company_name: j?.company_name ?? j?.companyName ?? null,
      eligibility: known
        ? "eligible"
        : hasEligibilityData
          ? "mismatch"
          : "unreported",
    });
  });

  const rank = { eligible: 0, unreported: 1, mismatch: 2 };
  return {
    hasEligibilityData,
    candidates: [...byId.values()].sort((a, b) => {
      const byRank = (rank[a.eligibility] ?? 3) - (rank[b.eligibility] ?? 3);
      if (byRank !== 0) return byRank;
      return compareCandidates(a, b);
    }),
  };
}

/** The agent's swaps the analyst has NOT unticked, as override records. */
export function agentOverrides(optimizationData, deselected, keyOf) {
  const reps = optimizationData?.normalized?.costOptimization?.replacements || [];
  return reps
    .filter((r) => !deselected?.[keyOf(r)])
    .map((r) => ({
      task_id: num(r.task_id),
      role: r.raci_role ? String(r.raci_role).toUpperCase() : null,
      replace_job_id: num(r.original_job_id ?? r.original_job?.job_id),
      new_job_id: num(r.new_job_id ?? r.new_job?.job_id),
      source: "agent",
    }))
    .filter((o) => o.task_id != null && o.replace_job_id != null && o.new_job_id != null);
}

/**
 * One override set from both inputs. A manual edit on the same slot (same task,
 * role and replaced job) REPLACES the agent's suggestion for that slot rather
 * than stacking on it — otherwise the backend would be told to replace the same
 * role-holder twice.
 */
export function mergeOverrides(agentList, manualMap) {
  const bySlot = new Map();
  (agentList || []).forEach((o) => {
    bySlot.set(overrideKey(o.task_id, o.role, o.replace_job_id), o);
  });
  Object.values(manualMap || {}).forEach((o) => {
    if (o?.task_id == null || o?.new_job_id == null) return;
    bySlot.set(overrideKey(o.task_id, o.role, o.replace_job_id), {
      task_id: num(o.task_id),
      role: o.role ? String(o.role).toUpperCase() : null,
      replace_job_id: num(o.replace_job_id),
      new_job_id: num(o.new_job_id),
      source: "manual",
      // Display fields ride along so the evidence blocks can name the change
      // without a second lookup. The wire-shape builders below drop them.
      new_job_name: o.new_job_name || null,
      new_job_rate: o.new_job_rate ?? null,
      from_job_name: o.from_job_name || null,
      from_job_rate: o.from_job_rate ?? null,
    });
  });
  // A manual edit that puts the original job back is not a change — drop it so
  // it is neither re-scored nor persisted.
  return [...bySlot.values()].filter(
    (o) => o.new_job_id != null && o.new_job_id !== o.replace_job_id,
  );
}

/** Wire shape for the what-if engine's `resource_overrides`. */
export const toResourceOverrides = (overrides) =>
  (overrides || []).map((o) => {
    const out = { task_id: o.task_id, job_id: o.new_job_id };
    if (o.replace_job_id != null) out.replace_job_id = o.replace_job_id;
    if (o.role) out.role = o.role;
    return out;
  });

/** Wire shape for the version save's `job_reassignments`. */
export const toJobReassignments = (overrides) =>
  (overrides || []).map((o) => {
    const out = {
      task_id: o.task_id,
      replace_job_id: o.replace_job_id,
      new_job_id: o.new_job_id,
    };
    if (o.role) out.role = o.role;
    return out;
  });

/**
 * Manual reassignments in the shape of `evidence.cost.resource_changes`, so the
 * AS-IS vs TO-BE evidence box and the Flow tab's "why the TO-BE moved" list can
 * explain a hand-picked change the same way they explain an agent-picked one.
 *
 * `savings_per_case` is deliberately omitted: it is a path-weighted figure only
 * the backend can compute, and a plausible-looking guess here would contradict
 * the very totals this block exists to justify.
 */
export function manualResourceChanges(overrides, taskNameById) {
  return (overrides || [])
    .filter((o) => o.source === "manual")
    .map((o) => ({
      task_id: o.task_id,
      task_name: taskNameById?.get?.(String(o.task_id)) || `Task ${o.task_id}`,
      from_job: o.from_job_name || `Job ${o.replace_job_id}`,
      from_rate: o.from_job_rate ?? null,
      to_job: o.new_job_name || `Job ${o.new_job_id}`,
      to_rate: o.new_job_rate ?? null,
      role: o.role || null,
      reason: "Reassigned by the analyst on the Analyze tab.",
      analyst_set: true,
    }));
}

/**
 * Improvement fractions in the shape the ranked scenario cards read, recomputed
 * from re-scored dimensions. Sign convention matches the cards: for time and
 * cost a POSITIVE value is a reduction; for quality and flexibility a positive
 * value is an increase.
 */
export function improvementsFromDimensions(dims) {
  const frac = (d, higherIsBetter) => {
    const a = num(d?.as_is);
    const b = num(d?.to_be);
    if (a == null || b == null || a === 0) return 0;
    return higherIsBetter ? (b - a) / Math.abs(a) : (a - b) / Math.abs(a);
  };
  return {
    time: frac(dims?.time, false),
    cost: frac(dims?.cost, false),
    quality: frac(dims?.quality, true),
    flexibility: frac(dims?.flexibility, true),
  };
}

/**
 * The per-job utilization rows (job-focused workload table) with every pending
 * override applied to their `assignments` list, so the job-focused table names
 * the same current holder the task-focused table does after a reassignment.
 *
 * Utilization/workload figures are deliberately left untouched: they are
 * company-wide numbers the backend measures from real data across every
 * process (see the table's own caption), and a manual reassignment here does
 * not change what the new job is actually, measurably working on until the
 * next full Optimize run re-measures it. A job newly added to the table by a
 * reassignment is marked `utilization_unmeasured` so the UI can say so rather
 * than implying a clean bill of health it never measured.
 */
export function applyOverridesToWorkloadJobs(jobs, manualMap, taskNameById) {
  const manual = Object.values(manualMap || {});
  const base = Array.isArray(jobs) ? jobs : [];
  if (!manual.length) return base;

  const list = base.map((j) => ({ ...j, assignments: [...(j.assignments || [])] }));
  const byJobId = new Map(list.map((j) => [String(j.job_id), j]));
  const sameSlot = (a, taskId, role) =>
    String(a.task_id) === String(taskId) && String(a.role || "").toUpperCase() === role;

  manual.forEach((o) => {
    if (o?.task_id == null || o?.new_job_id == null) return;
    const role = String(o.role || "").toUpperCase();
    const taskName = taskNameById?.get?.(String(o.task_id)) || `Task ${o.task_id}`;

    if (o.replace_job_id != null) {
      const fromJob = byJobId.get(String(o.replace_job_id));
      if (fromJob) {
        fromJob.assignments = fromJob.assignments.filter(
          (a) => !sameSlot(a, o.task_id, role),
        );
      }
    }

    let toJob = byJobId.get(String(o.new_job_id));
    if (!toJob) {
      toJob = {
        job_id: o.new_job_id,
        job_name: o.new_job_name || `Job ${o.new_job_id}`,
        utilization_percent: null,
        overutilized: false,
        band: null,
        weekly_workload_hrs: null,
        weekly_capacity_hrs: null,
        process_utilization_percent: null,
        process_weekly_hrs: null,
        assignments: [],
        utilization_unmeasured: true,
      };
      byJobId.set(String(o.new_job_id), toJob);
      list.push(toJob);
    }
    if (!toJob.assignments.some((a) => sameSlot(a, o.task_id, role))) {
      toJob.assignments.push({
        task_id: o.task_id,
        task_name: taskName,
        role,
        // Flags this row so the job-focused table can show which of a job's
        // assignments are new because of a reassignment, and name who it came
        // from — the same "was X" information the task-focused table shows,
        // read from the other side.
        reassigned: true,
        replaced_job_name: o.from_job_name || null,
      });
    }
  });

  // Scoped to "every job involved in this process" — a job reassigned away
  // from all of its tasks here no longer belongs on this table, even though
  // it may still hold others elsewhere.
  return list.filter((j) => (j.assignments || []).length > 0);
}

/**
 * The Overview-shaped RACI rows with every pending override applied, so the
 * task-focused table shows who WILL hold each role once the change is saved.
 * The replaced job is kept on the row (`replaced_job_name`) so the table can
 * show "was X" without a second lookup.
 */
export function applyOverridesToTaskGroups(taskGroups, manualMap) {
  const manual = Object.values(manualMap || {});
  if (!manual.length) return taskGroups;
  const bySlot = new Map(
    manual.map((o) => [overrideKey(o.task_id, o.role, o.replace_job_id), o]),
  );
  return (taskGroups || []).map((tg) => {
    let touched = false;
    const jobs = (tg.jobs || []).map((j) => {
      const o = bySlot.get(overrideKey(tg.task_id, j.role, j.job_id));
      if (!o) return j;
      touched = true;
      return {
        ...j,
        job_id: o.new_job_id,
        name: o.new_job_name || `Job ${o.new_job_id}`,
        hourly_rate: o.new_job_rate ?? j.hourly_rate ?? null,
        replaced_job_id: j.job_id,
        replaced_job_name: j.name,
        // The outgoing holder's own rate, kept so re-editing this row prices
        // candidates against where the row STARTED rather than against the
        // replacement chosen a moment ago.
        replaced_job_rate: j.hourly_rate ?? null,
        overridden: true,
      };
    });
    return touched ? { ...tg, jobs } : tg;
  });
}
